import Axios, { AxiosInstance, AxiosRequestConfig, CancelTokenSource } from 'axios';

import { showNotification } from '~src/store/app/actions';
import { accountSuspended } from '~src/store/session/actions';

import { store } from '..';
import { apiConfig } from '../../config/server/server.index';

import { addDebugText } from './addDebugText';
import { Base } from './api/base';
import { PulseCheck } from './api/pulseCheck';
import { Reporting } from './api/reporting';

interface ApiConfI {
  base_url: string;
  apiVerStoreKey?: string;
}

interface HeadersI {
  [key: string]: string;
}

export enum CancelReasons {
  SIMILAR_CALL_DISCOVERED = 'SIMILAR_CALL_DISCOVERED',
  CANCELING_ALL_TOKENS = 'CANCELING_ALL_TOKENS',
  DEBOUNCE = 'DEBOUNCE',
}

const checkApiVersion = (headers: HeadersI, storageName: string) => {
  let ver;
  const storedVer = localStorage.getItem(storageName);
  const headersKeys = Object.keys(headers);
  for (const key of headersKeys) {
    if (key.toLowerCase() === 'x-api-version') {
      ver = headers[key];
      break;
    }
  }
  if (!storedVer && ver) {
    localStorage.setItem(storageName, ver);
  } else if (ver && ver !== storedVer) {
    localStorage.setItem(storageName, ver);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }
};

const callsMap: Record<string, CancelTokenSource> = {};
const getCallKey = ({ method, baseURL, url }: AxiosRequestConfig) => `${method}:${baseURL}${url}`;

const cancelAllTokens = (exceptKey?: string) => {
  const callKeys = Object.keys(callsMap);
  console.info(`canceling all requests: [${callKeys.join(',')}] except [${exceptKey || ''}]`);
  callKeys.forEach((key) => {
    if (key !== exceptKey) {
      callsMap[key].cancel(CancelReasons.CANCELING_ALL_TOKENS);
    }
  });
};

function createApi({ base_url, apiVerStoreKey }: ApiConfI): AxiosInstance {
  const api = Axios.create({
    baseURL: base_url,
  });

  if (!api.defaults.headers) {
    api.defaults.headers = {};
  }
  api.defaults.headers['Content-Type'] = 'application/json';

  api.interceptors.request.use(
    (config: AxiosRequestConfig) => {
      // Ensure exactly one slash between base url and url
      if (config.url) {
        config.url = '/' + config.url.replace(/^\/+/, '');
      }
      if (config.baseURL) {
        config.baseURL = config.baseURL.replace(/\/+$/, '');
      }
      if (
        config.url &&
        config.url !== '/company' &&
        config.url !== '/keyresults' &&
        config.url !== '/objectives?filter.stream=true'
      ) {
        const callKey = getCallKey(config);
        const call = callsMap[callKey];
        if (call) {
          call.cancel(CancelReasons.SIMILAR_CALL_DISCOVERED);
        }
        callsMap[callKey] = Axios.CancelToken.source();
        if (!config.cancelToken) {
          config.cancelToken = callsMap[callKey].token;
        }
      }
      const token = localStorage.getItem('jwt');
      if (token) {
        if (!config.headers) {
          config.headers = {};
        }
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  api.interceptors.response.use(
    (resp) => {
      const callKey = getCallKey(resp.config);
      if (callsMap[callKey]) {
        delete callsMap[callKey];
      }
      if (document.location.search.indexOf('debug=1') > -1) {
        addDebugText(
          `${resp.config.url}, ${resp.config.method}, ${resp.status}, ${Object.keys(resp.headers)
            .map((k: string) => k + ': ' + resp.headers[k])
            .join(', ')}, ${resp.data}`,
        );
      }
      if (apiVerStoreKey) {
        checkApiVersion(resp.headers, apiVerStoreKey);
      }
      return resp;
    },
    (error) => {
      const { response, config, message } = error;
      const callKey = config ? getCallKey(config) : message;
      if (callsMap[callKey]) {
        delete callsMap[callKey];
      }
      if (document.location.search.indexOf('debug=1') > -1) {
        addDebugText(`${config.url}, ${config.method}, ${response && response.status},
          ${response &&
          Object.keys(response.headers)
            .map((k: string) => k + ': ' + response.headers[k])
            .join(', ')
          }, ${response && response.data}, ${message}`);
      }

      if (!response) {
        if (!(message in CancelReasons)) {
          console.warn(`no response for axios request ${callKey}, message:`, message);
        }
        return Promise.reject(error);
      }

      if (
        response.status === 403 &&
        response.headers['x-tenant-activated'] === 'false' &&
        // don't block the screen for this specific request
        // for the multiple accounts page
        response.config.url.split('/').splice(-1).pop() !== 'company'
      ) {
        cancelAllTokens(callKey);
        store.dispatch(accountSuspended);
        return Promise.reject(error);
      }

      if (response.status > 400 && response.status !== 422) {
        store.dispatch(
          showNotification({
            id: 'x.loading_error.message',
            txtKey:
              response.data && response.data.error && typeof response.data.error === 'string'
                ? `x.error.${response.data.error}`
                : 'x.loading_error.message',
            txtProps: { url: error.config.url || '', status: response.status },
            type: 'error',
            persistent: true,
          }),
        );
        return Promise.reject(error);
      }

      return Promise.reject(error);
    },
  );
  return api;
}

export const baseApi = createApi(apiConfig.api);
export const defaultBaseApi = new Base(baseApi);
export const pulseCheck = createApi(apiConfig.pulse_check);
export const defaultPulseCheckApi = new PulseCheck(pulseCheck);
export const reportingApi = createApi(apiConfig.reporting);
export const defaultReportingApi = new Reporting(reportingApi);
export const directFeedbackApi = createApi(apiConfig.direct_feedback);
export const lxAppApi = createApi(apiConfig.lxApp);
export const okrApi = createApi(apiConfig.okr);
export const userServiceApi = createApi(apiConfig.userServiceApi);
export const notesApi = createApi(apiConfig.notes);